import { Modal, Slider, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  calculateCropPreview,
  calculateSourceCrop,
  clampCropValue,
  cropHasTargetResolution,
} from './image-crop-geometry';
import type { CropPoint as Point, CropSize as Size } from './image-crop-geometry';

export type ImageCropConfig = {
  width: number;
  height: number;
  aspectLabel: string;
  maxBytes: number;
  preserveTransparency?: boolean;
};

type Props = {
  config: ImageCropConfig;
  file: File | null;
  label: string;
  open: boolean;
  onCancel: () => void;
  onConfirm: (file: File) => Promise<void>;
};

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('图片压缩失败')), 'image/webp', quality);
  });
}

function byteLabel(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)}MB` : `${Math.round(bytes / 1024)}KB`;
}

export function ImageCropModal({ config, file, label, open, onCancel, onConfirm }: Props) {
  const imageRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ pointerId: number; x: number; y: number; pan: Point } | null>(null);
  const [stageElement, setStageElement] = useState<HTMLDivElement | null>(null);
  const [objectUrl, setObjectUrl] = useState('');
  const [natural, setNatural] = useState<Size>({ width: 0, height: 0 });
  const [stage, setStage] = useState<Size>({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [processing, setProcessing] = useState(false);
  const aspect = config.width / config.height;
  const previewWidth = Math.min(400, Math.round(520 * aspect));

  useEffect(() => {
    if (!file) { setObjectUrl(''); return; }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    setNatural({ width: 0, height: 0 });
    setZoom(1);
    setPan({ x: 0, y: 0 });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!open || !stageElement) return;
    const updateSize = () => {
      const rect = stageElement.getBoundingClientRect();
      if (rect?.width && rect.height) setStage({ width: rect.width, height: rect.height });
    };
    const animationFrame = window.requestAnimationFrame(updateSize);
    const observer = new ResizeObserver(updateSize);
    observer.observe(stageElement);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [open, aspect, stageElement]);

  const bindStage = useCallback((node: HTMLDivElement | null) => setStageElement(node), []);

  const geometry = useMemo(() => calculateCropPreview(natural, stage, zoom, pan), [natural, pan, stage, zoom]);
  const previewReady = natural.width > 0 && natural.height > 0 && stage.width > 0 && stage.height > 0;

  useEffect(() => {
    if (pan.x !== geometry.safePan.x || pan.y !== geometry.safePan.y) setPan(geometry.safePan);
  }, [geometry.safePan.x, geometry.safePan.y, pan.x, pan.y]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!previewReady) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, pan: geometry.safePan };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setPan({
      x: clampCropValue(drag.pan.x + event.clientX - drag.x, -geometry.maxX, geometry.maxX),
      y: clampCropValue(drag.pan.y + event.clientY - drag.y, -geometry.maxY, geometry.maxY),
    });
  };

  const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  };

  const crop = async () => {
    const image = imageRef.current;
    if (!file || !image || !image.complete || !natural.width || !natural.height) {
      message.warning('图片仍在加载，请稍后再试');
      return;
    }
    setProcessing(true);
    try {
      const source = calculateSourceCrop(natural, stage, geometry);
      if (!cropHasTargetResolution(source, config)) {
        throw new Error(`当前裁剪区清晰度不足，请上传至少 ${config.width}×${config.height} 的图片或减小缩放`);
      }
      const canvas = document.createElement('canvas');
      canvas.width = config.width;
      canvas.height = config.height;
      const context = canvas.getContext('2d', { alpha: true });
      if (!context) throw new Error('浏览器不支持图片裁剪');
      context.clearRect(0, 0, config.width, config.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(image, source.x, source.y, source.width, source.height, 0, 0, config.width, config.height);
      let blob: Blob | null = null;
      for (const quality of [0.9, 0.82, 0.74, 0.66, 0.58, 0.5, 0.42, 0.34]) {
        blob = await canvasToBlob(canvas, quality);
        if (blob.size <= config.maxBytes) break;
      }
      if (!blob) throw new Error('图片压缩失败');
      if (blob.size > config.maxBytes) throw new Error(`图片压缩后仍超过 ${byteLabel(config.maxBytes)}，请换一张图片再试`);
      const baseName = file.name.replace(/\.[^.]+$/, '').slice(0, 60) || 'image';
      await onConfirm(new File([blob], `${baseName}-${config.width}x${config.height}.webp`, { type: 'image/webp', lastModified: Date.now() }));
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  return <Modal
    open={open}
    title={`裁剪${label}（${config.aspectLabel}）`}
    okText="裁剪并上传"
    cancelText="取消"
    confirmLoading={processing}
    maskClosable={false}
    onOk={crop}
    onCancel={onCancel}
    width={Math.max(460, previewWidth + 120)}
  >
    <Typography.Paragraph type="secondary">
      拖动图片选择区域，使用滑块缩放。上传前会输出为 {config.width}×{config.height} WebP 并压缩。{config.preserveTransparency ? '将保留透明背景。' : ''}
    </Typography.Paragraph>
    <div
      ref={bindStage}
      className="avatar-crop-stage"
      style={{ width: `min(${previewWidth}px, calc(100vw - 96px))`, aspectRatio: `${config.width} / ${config.height}` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
    >
      {objectUrl ? <img
        ref={imageRef}
        src={objectUrl}
        alt={`${label}裁剪预览`}
        draggable={false}
        onLoad={event => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
        style={{
          width: geometry.width,
          height: geometry.height,
          left: geometry.left,
          top: geometry.top,
          visibility: previewReady ? 'visible' : 'hidden',
        }}
      /> : null}
      <div className="avatar-crop-grid" />
    </div>
    <div className="avatar-crop-zoom"><span>缩放</span><Slider min={1} max={3} step={0.01} value={zoom} onChange={setZoom} /></div>
  </Modal>;
}
